import { useMemo, useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { SkippedFilesExplainerModal } from './skipped-files-explainer-modal';
import { UploadFailureReason } from '../../../../../../../../../../backend/src/types/types';
import { FAILURE_REASON_CONFIG } from '../../utils';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSkippedFiles } from '@/api/queries/useGetSkippedFiles';
import { SkippedFile } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { downloadSkippedFilesReport } from '@/helpers/report-utils';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/new/components/shadcn/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { ScrollableTable } from '@/new/components/table/scrollable-table';

const columnHelper = createColumnHelper<SkippedFile>();

interface SkippedFilesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
}

export function SkippedFilesModal({ open, onOpenChange, matterId }: SkippedFilesModalProps) {
  const [globalFilter, setGlobalFilter] = useState('');
  const [explainerModalOpen, setExplainerModalOpen] = useState(false);
  const { data: skippedFiles, isLoading } = useGetSkippedFiles(matterId);
  const { data: matter } = useGetMatterV2(matterId);

  const hasRelativityEnabled = !!matter?.relativityWorkspaceId;

  const columns = useMemo(
    () => [
      columnHelper.accessor('filename', {
        header: () => <span>Filename</span>,
        cell: (info) => (
          <div className="flex items-center gap-2">
            <Icon className="lucide--file h-3.5 w-3.5 text-gray-400" />
            <span className="text-sm text-gray-800">{info.getValue()}</span>
          </div>
        ),
      }),
      ...(hasRelativityEnabled
        ? [
            columnHelper.accessor('controlNumber', {
              header: () => <span>Control Number</span>,
              cell: (info) => {
                const value = info.getValue();
                return value ? (
                  <span className="text-sm text-gray-700">{value}</span>
                ) : (
                  <span className="text-sm text-gray-400">—</span>
                );
              },
            }),
          ]
        : []),
      columnHelper.accessor('rejectedReason', {
        header: () => <span>Reason</span>,
        cell: (info) => {
          const reason = info.getValue();
          const config = FAILURE_REASON_CONFIG[reason as keyof typeof FAILURE_REASON_CONFIG];
          if (!config) {
            return <span className="text-sm text-gray-500">{reason}</span>;
          }
          return (
            <div className="flex items-center gap-1.5">
              <Icon className={`${config.icon} text-amber-500`} />
              <span className="text-sm text-gray-700">{config.label}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor('duplicateFile', {
        header: () => <span>Duplicate Of</span>,
        cell: (info) => {
          const reason = info.row.original.rejectedReason;
          if (reason !== UploadFailureReason.Duplicate) {
            return <span className="text-sm text-gray-400">—</span>;
          }

          const duplicateFile = info.getValue();
          if (!duplicateFile) return <span className="text-sm text-gray-400">—</span>;

          return (
            <div className="flex items-center gap-2">
              {duplicateFile.doc?.id ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="h-auto px-0 py-0 font-medium text-blue-500"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        // In a new tab open the document
                        if (!matter?.allDocumentsCaseId) return;
                        window.open(
                          `/matters/${matterId}/subsets/${matter?.allDocumentsCaseId}/docs/${duplicateFile.doc?.id}`,
                          '_blank',
                        );
                      }}
                    >
                      {duplicateFile.name.slice(0, 12)}...
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{duplicateFile.name}</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-sm text-gray-600">{duplicateFile.name}</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{duplicateFile.name}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor('createdDate', {
        header: () => <span>Date</span>,
        cell: (info) => <span className="text-sm text-gray-600">{formatDate(info.getValue(), true, true)}</span>,
      }),
    ],
    [matter?.allDocumentsCaseId, hasRelativityEnabled],
  );

  const table = useReactTable({
    data: skippedFiles ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
  });

  return (
    <>
      <SkippedFilesExplainerModal open={explainerModalOpen} onOpenChange={setExplainerModalOpen} />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[80vh] max-w-6xl flex-col">
          <DialogHeader>
            <DialogTitle>Skipped Files</DialogTitle>
            <DialogDescription>
              Files that were not uploaded due to validation rules (duplicates, unsupported formats, etc.)
            </DialogDescription>
          </DialogHeader>

          <div className="mb-3 flex items-center gap-2">
            <Button
              variant="default"
              onClick={() => {
                trackCustomEvent(TrackingEventsV2.FileHistoryDownloadSkippedReport, { matterId });
                skippedFiles &&
                  downloadSkippedFilesReport(skippedFiles, matterId, { includeControlNumber: hasRelativityEnabled });
              }}
              disabled={!skippedFiles || skippedFiles.length === 0}
            >
              <Icon className="lucide--download h-3.5 w-3.5" />
              Download Report
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                trackCustomEvent(TrackingEventsV2.FileHistoryOpenSkippedExplainer, { matterId });
                setExplainerModalOpen(true);
              }}
            >
              <Icon className="lucide--help-circle h-3.5 w-3.5" />
              Why are files skipped?
            </Button>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden">
            <ScrollableTable
              table={table}
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
              plural="skipped files"
              loading={isLoading}
              className="h-full max-h-[50vh] p-0"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
